import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import * as Yup from 'yup';

import * as fonts from '../../util/fonts';
import {functionalitiesAliasMap, strings} from '../../util/strings';
import {AppForm} from '../../components/forms';
import {getUserMemId, numberWithSpaces} from '../../util/utility';
import {get} from '../../api/api';
import {
  uriGetProductMembers,
  uriGetThirdPartyAuthorizedUsers,
  uriMemberData,
} from '../../api/endpoints';
import {useLocation} from 'react-router-dom';
import {
  setIban,
  setIbanFilterOpenTrigger,
  setSelectedBu,
  setTitular,
  setUpdateRoutesNewSelectedPatient,
} from '../../store/actions';
import {Column, Row} from '../../components/layout';
import IbanFilter from '../my-data/iban-filter/';
import AppSpinner from '../../components/app-spinner';
import InternationalMyInfoData from '../my-data/my-info-int/InternationalMyInfoData';
import AppSimpleAccordion from '../../components/app-simple-accordion';
import moment from 'moment';
import '../my-data/styles.scss';

const validationSchema = Yup.object().shape({});

interface PersonalDataProps {
  labels: typeof strings.pt.myData;
  selectedBu: any;
  userBankAccountDataWasUpdated: boolean;
  availableBus: string[];
  user: any;
  ibanStrings: typeof strings.pt.ibanRefunds;
  titular: any;
  bankAccountDataHolder: any;
  busMap: any;
  buStyles: any;
  busGroupExceptions: any;
  busFuncMap: any;
  setIbanFilterOpenTrigger: (value: any) => void;
  setSelectedBu: (value: any) => void;
  setIban: (value: any) => void;
  setTitular: (value: any) => void;
  setUpdateRoutesNewSelectedPatient: () => void;
}

const PersonalData = ({
  labels,
  selectedBu,
  userBankAccountDataWasUpdated,
  availableBus,
  user,
  ibanStrings,
  titular,
  bankAccountDataHolder,
  busMap,
  buStyles,
  busGroupExceptions,
  busFuncMap,
  setIbanFilterOpenTrigger,
  setSelectedBu,
  setIban,
  setTitular,
  setUpdateRoutesNewSelectedPatient,
}: PersonalDataProps) => {
  const allLabels = {...strings.pt.myData, ...labels};
  const featureName = 'MyData';
  const location: any = useLocation();
  const groupIdExceptionMap = busGroupExceptions?.filter(
    (exception: any) => exception.buAlias === selectedBu.buId,
  );
  const grpIdObject = groupIdExceptionMap?.find(
    (obj: any) => obj.groupId === selectedBu.grpId,
  );
  const myDataFooterContactFromGroupId =
    grpIdObject?.groupExceptionDetails[0].myInsuranceDataFooterContact;
  const cardUserDOB: string = selectedBu.dob; // Format: 2021-09-04

  const [memId, setMemId] = useState('');
  const [multicode, setMulticode] = useState('');
  const [insurancePerson, setInsurancePerson] = useState(selectedBu.memberName);
  const [insurancePersons, setInsurancePersons] = useState<any[]>([]);
  const [selectedInsuranceData, setSelectedInsuranceData] = useState<any>();
  const [selectedInsurancePerson, setselectedInsurancePerson] =
    useState<any>(selectedBu);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [insurancePersonOptions, setInsurancePersonOptions] = useState<any>([]);
  const [notAuth, setNotAuth] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const isTitular = () => {
    if (ibanStrings?.ibanRefunds_ibanIsEnabled !== '1') {
      setCanSubmit(false);
    } else {
      if (
        bankAccountDataHolder === 'SUBSCRIBER'
          ? titular?.nif === selectedBu?.nif &&
            titular?.buId === selectedBu?.buId &&
            titular?.memId === selectedBu?.memId
          : true
      ) {
        setCanSubmit(true);
      } else {
        setCanSubmit(false);
      }
    }
  };

  const updateSelectedCard = () => {
    localStorage.setItem('BuId', location?.state?.patientSelected?.buId);
    const completeSelectedBu = busMap?.[selectedBu?.buId]?.[
      selectedBu?.famId
    ]?.find((el: any) => {
      return el.memId === location?.state?.patientSelected?.memId;
    });
    setSelectedBu(completeSelectedBu);
    setUpdateRoutesNewSelectedPatient();
  };

  const updateIban = (iban: string) => {
    setIban(iban);
    setTitular({...titular, iban: iban});
  };

  const isMinor = () => {
    const today = new Date();
    const birthDate = new Date(cardUserDOB);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age < 18;
  };

  const handleSelectedDataChange = (insurancePerson: any) => {
    async function getSelectedInsuranceData() {
      setIsLoading(true);
      try {
        get(
          uriMemberData(
            insurancePerson.buId,
            insurancePerson.memId,
            insurancePerson.famId,
          ),
          false,
          0,
          featureName,
        ).then((res: any) => {
          setSelectedInsuranceData(res.data);
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        });
        if (ibanStrings?.ibanRefunds_bankAccountDataHolder === 'SUBSCRIBER') {
          if (
            insurancePerson.memId === memId ||
            insurancePerson.memId === multicode ||
            (memId === '' && multicode === '')
          ) {
            setNotAuth(false);
          } else {
            get(
              uriGetThirdPartyAuthorizedUsers(
                selectedBu.buId,
                insurancePerson.memId,
                memId,
              ),
              false,
              0,
              featureName,
            ).then((authUsersRes: any) => {
              if (
                authUsersRes.data?.items[0] &&
                !authUsersRes.data?.items[0].authorized
              ) {
                setNotAuth(true);
              } else {
                setNotAuth(false);
              }
            });
          }
        }
        if (ibanStrings?.ibanRefunds_form === 'servio') {
          if (ibanStrings?.ibanRefunds_usePermissions === '1') {
            if (insurancePerson.nif === user?.nif) {
              setNotAuth(false);
            } else {
              get(
                uriGetThirdPartyAuthorizedUsers(
                  selectedBu.buId,
                  insurancePerson.memId,
                  memId,
                ),
                false,
                0,
                featureName,
              ).then((authUsersRes: any) => {
                // If user backAccoundDataHolder is "INDIVIDUAL" and is under 18 years old, give access to change IBAN
                if (isMinor()) {
                  setNotAuth(false);
                  return;
                }

                if (
                  authUsersRes.data?.items[0] &&
                  !authUsersRes.data?.items[0].authorized
                ) {
                  setNotAuth(true);
                } else {
                  setNotAuth(false);
                }
              });
            }
          }
        }
      } catch (err: any) {
        setNotAuth(false);
      }
    }
    getSelectedInsuranceData();
  };

  const handleSelectedPerson = (id: string) => {
    insurancePersons.forEach((person) => {
      if (person.id === id) {
        setselectedInsurancePerson(person);
      }
    });
  };

  const renderSerbianForm = () => {
    return (
      <>
        {selectedInsuranceData && (
          <InternationalMyInfoData
            buStyles={buStyles}
            user={user}
            selectedInsuranceData={selectedInsuranceData}
            labels={labels}
            editable={ibanStrings?.ibanRefunds_ibanIsEnabled === '1'}
            noAuth={notAuth}
            serviceLabels={allLabels}
          />
        )}
        <div className="my-info-wrapper">
          <AppSimpleAccordion
            openByDefault={labels?.myData_insuranceDataExpanded === '1'}
            header={
              <div className="accordion-title-wrapper">
                <p style={{...fonts.M}}>
                  {labels?.myData_IntInsuranceDataHeader}
                </p>
              </div>
            }
            content={
              <>
                <Row className="my-data-personal-row-sr">
                  <Column className="my-data-personal-left-column">
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_fullName}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {selectedInsuranceData.name}
                    </Row>
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_dateOfBirth}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {moment(selectedInsuranceData.dob)
                        .format('DD-MM-YYYY')
                        .toUpperCase()}
                    </Row>
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_gender}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {selectedInsuranceData.genderCode}
                    </Row>
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_fiscalNumber}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {selectedInsurancePerson.nif}
                    </Row>
                  </Column>
                  <Column className="my-data-personal-right-column">
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_address}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {selectedInsuranceData.addr} <br />
                      {selectedInsuranceData.postalcode} <br />
                      {selectedInsuranceData.city}
                    </Row>
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_phone}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {numberWithSpaces(selectedInsuranceData.cellPhone)}
                    </Row>
                    {labels?.myData_cellphone_show === '1' && (
                      <>
                        <Row className="account-data-row-title">
                          <p className="m-0" style={{...fonts.SM}}>
                            {labels?.myData_cellphone}
                          </p>
                        </Row>
                        <Row className="my-data-row-sr">
                          {numberWithSpaces(selectedInsuranceData.cellPhone)}
                        </Row>
                      </>
                    )}
                    <Row className="account-data-row-title">
                      <p className="m-0" style={{...fonts.SM}}>
                        {labels?.myData_email}
                      </p>
                    </Row>
                    <Row className="my-data-row-sr">
                      {selectedInsuranceData.email}
                    </Row>
                  </Column>
                </Row>
                <Column className="my-data-adicional-help">
                  <Row style={{...fonts.M, ...fonts.Bold}}>
                    {labels?.myData_incorrectData}
                  </Row>
                  <Row style={{...fonts.M, ...fonts.Bold}}>
                    {myDataFooterContactFromGroupId
                      ? myDataFooterContactFromGroupId
                      : labels?.myData_contact}
                  </Row>
                </Column>
              </>
            }
          />
        </div>
      </>
    );
  };

  const renderDefaultForm = () => {
    return (
      <>
        <div className="my-data-personal-row">
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_fullName}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {selectedInsuranceData.name}
            </p>
          </div>
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_address}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {selectedInsuranceData.addr
                ? `${selectedInsuranceData.addr}, ${selectedInsuranceData.postalcode} ${selectedInsuranceData.city}`
                : ''}
            </p>
          </div>
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_dateOfBirth}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {moment(selectedInsuranceData.dob)
                .format('DD-MM-YYYY')
                .toUpperCase()}
            </p>
          </div>
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_phone}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {numberWithSpaces(selectedInsuranceData.cellPhone)}
            </p>
          </div>
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_gender}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {selectedInsuranceData.genderCode}
            </p>
          </div>
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_fiscalNumber}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {selectedInsurancePerson.nif}
            </p>
          </div>
          <div className="my-data-personal-item">
            <p style={{...fonts.SM}}>{labels?.myData_email}</p>
            <p style={{...fonts.M, ...fonts.SemiBold}}>
              {selectedInsuranceData?.email || ''}
            </p>
          </div>
        </div>
        <div className="my-data-adicional-help">
          <div style={{...fonts.M, ...fonts.Bold}}>
            {labels?.myData_incorrectData}
          </div>
          <div style={{...fonts.M, ...fonts.Bold}}>
            {myDataFooterContactFromGroupId
              ? myDataFooterContactFromGroupId
              : labels?.myData_contact}
          </div>
        </div>
      </>
    );
  };
  useEffect(() => {
    setInsurancePerson(selectedBu?.memberName);
  }, [selectedBu?.memberName]);

  useEffect(() => {
    handleSelectedPerson(insurancePerson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurancePerson]);

  useEffect(() => {
    !!selectedInsurancePerson &&
      handleSelectedDataChange(selectedInsurancePerson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInsurancePerson, userBankAccountDataWasUpdated]);

  useEffect(() => {
    const loggedInsurancePerson = getUserMemId(availableBus, user, selectedBu);

    async function getInsurancePersonOptions() {
      const insurancePersonsGet = await get(
        uriGetProductMembers(user.nif, user.birthdate),
        false,
        0,
        featureName,
      ).then((res: any) => {
        return res?.data?.items;
      });
      let newInsurances: any[] = [];
      let options: any[] = [];

      (insurancePersonsGet as any).forEach((insurancePerson: any) => {
        if (
          selectedBu.famId === insurancePerson.famId &&
          selectedBu.buId === insurancePerson.buId
        ) {
          insurancePerson.productList.forEach((person: any) => {
            if (person) {
              const id = person.memberName;
              newInsurances.push({...person, id});
              options.push({
                value: person.memberName,
                label: person.memberName,
              });
            }
          });
        }
      });

      setInsurancePersons(newInsurances);
      setselectedInsurancePerson(
        newInsurances?.find(
          (el: any) => el?.memberName === selectedBu?.memberName,
        ) || newInsurances[0],
      );
      setInsurancePersonOptions(options);
    }
    setMemId(loggedInsurancePerson?.memId || '');
    setMulticode(loggedInsurancePerson.multicode);
    getInsurancePersonOptions();
    isTitular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBu.memId]);

  useEffect(() => {
    isTitular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ibanStrings?.ibanRefunds_ibanIsEnabled]);

  useEffect(() => {
    if (titular) {
      isTitular();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titular]);

  useEffect(() => {
    let filterPatients = insurancePersonOptions?.filter(
      (x: any) => x !== undefined,
    );

    setInsurancePersonOptions(filterPatients);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurancePersons]);

  useEffect(() => {
    if (location?.state?.patientSelected) {
      setTimeout(() => {
        setIbanFilterOpenTrigger({ibanFilter: true});
      }, 1000);
      selectedBu?.memId !== location?.state?.patientSelected?.memId &&
        updateSelectedCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  return (
    <>
      <AppForm
        initialValues={{
          insurancePerson: insurancePerson, // alterei isto para as minhas options
        }}
        validationSchema={validationSchema}
        onSubmit={() => {}}>
        {!!selectedInsuranceData && !isLoading ? (
          <>
            {busFuncMap ? (
              busFuncMap[selectedBu.buId]?.map((func: any) => {
                if (
                  !func?.isVisible ||
                  functionalitiesAliasMap.iban !== func?.featureTemplateAlias
                ) {
                  return null;
                }
                return (
                  <>
                    <p
                      style={{
                        ...fonts.M,
                        ...fonts.Bold,
                      }}>
                      {allLabels?.myData_iBANReimbursement}
                    </p>
                    <p
                      style={{
                        ...fonts.M,
                      }}>
                      {allLabels?.myData_iBANTitle}
                    </p>

                    {ibanStrings?.ibanRefunds_form !== 'servio' && (
                      <IbanFilter
                        iBAN={titular?.iban}
                        updateIban={(iban: string) => updateIban(iban)}
                        accountOwner={
                          location?.state?.patientSelected?.name ||
                          titular?.name
                        }
                        userFilter={titular}
                        currentUser={user}
                        canSubmit={canSubmit}
                      />
                    )}
                  </>
                );
              })
            ) : (
              <></>
            )}
            <>
              <div className="my-data-validation-container"></div>
              <p
                style={{
                  ...fonts.M,
                  ...fonts.Bold,
                }}>
                {allLabels?.myData_IntInsuranceDataHeader}
              </p>
              {ibanStrings?.ibanRefunds_form === 'servio'
                ? renderSerbianForm()
                : renderDefaultForm()}
            </>
          </>
        ) : (
          <AppSpinner className="spinner-my-data" />
        )}

        {notAuth &&
        ibanStrings?.ibanRefunds_bankAccountDataHolder === 'SUBSCRIBER' ? (
          <Column className="my-data-permission">
            <Row style={{...fonts.M}}>{labels?.myData_noPermission}</Row>
          </Column>
        ) : null}
      </AppForm>
    </>
  );
};

const mapStateToProps = ({
  strings,
  selectedBu,
  userBankAccountDataWasUpdated,
  availableBus,
  user,
  titular,
  bankAccountDataHolder,
  busMap,
  globalStyles,
  busGroupExceptions,
  busFuncMap,
}: any) => ({
  labels: strings.value.pt.myData,
  selectedBu: selectedBu.value,
  userBankAccountDataWasUpdated: userBankAccountDataWasUpdated.value,
  availableBus: availableBus.value,
  user: user.value,
  ibanStrings: strings.value.pt.ibanRefunds,
  titular: titular.value,
  bankAccountDataHolder: bankAccountDataHolder.value,
  busMap: busMap.value,
  buStyles: globalStyles.value,
  busGroupExceptions: busGroupExceptions.value,
  busFuncMap: busFuncMap.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setIbanFilterOpenTrigger: (value: any) =>
    dispatch(setIbanFilterOpenTrigger(value)),
  setSelectedBu: (value: any) => dispatch(setSelectedBu(value)),
  setUpdateRoutesNewSelectedPatient: () =>
    dispatch(setUpdateRoutesNewSelectedPatient()),
  setIban: (value: any) => dispatch(setIban(value)),
  setTitular: (value: any) => dispatch(setTitular(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(PersonalData),
);
